<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import BaseModal from '../wrappers/BaseModal.vue'
import { useModelWrapper } from '../wrappers/helpers/useModelWrapper'
import { useCodes } from '../../store/useCodes'

const props = defineProps<{
  modelValue: boolean
  checkedTaskIds: string[]
}>()
const emit = defineEmits<{
  (e: 'setBulkRaiFilter'): void
  (e: 'update:modelValue'): void
  (e: 'updateRaiCode', data: { rai_code: any; rai_reason_code: any }): any
}>()
/**Controls if popup shows or not */
const value = useModelWrapper(props, emit)
const { t } = useI18n({ useScope: 'global' })
const label = (key: string) => t(`form.${key}`)
const close = () => {
  // Close the modal
  value.value = false
}
const update = async () => {
  if (await valid()) {
    emit('updateRaiCode', { rai_code: selection.value, rai_reason_code: reasonSelection.value })
    // Close modal
    value.value = false
  }
}
const selection = ref()
const selectionRef = ref()
/** Updates selection and clears stale reason selection */
const updateSelection = (val: any) => {
  reasonSelection.value = undefined
  selection.value = val
}
const { bulkUpdateRaiCodes: options } = storeToRefs(useCodes())
const { getReasonCodesByRaiCodeId, isRaiApplicableForTask } = useCodes()
const reasonSelection = ref()
const reasonSelectionRef = ref()
const reasonOptions = computed(() => {
  if (!selection.value) return []
  else {
    return getReasonCodesByRaiCodeId(selection.value.id)
  }
})

/**
 * Run quasar's validation and return if conditions are met
 */
const valid = async () => {
  let selValid = true
  let reasValid = true
  //run validations
  if (selectionRef.value) {
    selValid = await selectionRef.value.validate()
  }
  if (reasonSelectionRef.value) {
    reasValid = await reasonSelectionRef.value.validate()
  }
  //if a reason code selection, check its valid, otherwise just check if rai code selection valid
  if (reasonSelectionRef.value) {
    return selValid && reasValid
  }
  return selValid
}
</script>
<template>
  <BaseModal v-model="value">
    <template #title> {{ label('bulkUpdateRai') }} </template>
    <template #content>
      <div>
        {{ label('bulkUpdateRaiNote') }}
        <a class="text-primary underline cursor-pointer" @click="$emit('setBulkRaiFilter')">
          {{ label('bulkUpdateRaiFilter') }}
        </a>
      </div>
      <q-select
        filled
        ref="selectionRef"
        :model-value="selection"
        @update:model-value="updateSelection"
        :options="options"
        :rules="[
          (val: any) => val || label('requiredGeneric'),
          (val: any) => checkedTaskIds.every(id => isRaiApplicableForTask(val.code, String(id))) || label('bulkUpdateRaiNotApplicable')
        ]"
        color="accent"
        label="RAI Code"
        behavior="menu"
      />
      <q-select
        filled
        ref="reasonSelectionRef"
        v-if="reasonOptions.length"
        v-model="reasonSelection"
        :options="reasonOptions"
        :rules="[(val: any) => val || label('requiredGeneric')]"
        color="accent"
        label="RAI Reason Code"
        behavior="menu"
      />
    </template>
    <template #actions>
      <q-btn rounded flat @click="close" :label="label('close')" />
      <q-btn rounded color="primary" @click="update">
        <span class="q-px-sm">{{ label('update') }}</span>
      </q-btn>
    </template>
  </BaseModal>
</template>
